/**
 * lib/attribution.js (r7.7, 077) — where did this buyer come from?
 *
 * Captured once per browser session on the first page that carries a `utm_*` parameter (or, failing that, the
 * first landing with an external referrer), kept in sessionStorage and forwarded on booking create as `utm`.
 * booking-service writes one marketing.attribution row per booking; the Studio's Results tab reads it back as
 * "attributed vs direct" per campaign. No cookies, no third-party call, nothing leaves the browser until the
 * customer books — a session that never books leaves no trace.
 *
 *   captureAttribution(location)   — router listener + boot; first-touch wins for the session
 *   utmPayload()                   — { source, medium, campaign, content, term, session_id, referrer, landed_at } | null
 *   attributionSummary()           — for the confirmation copy ("You came from Instagram")
 */
const KEY = 'snt_utm_v1';
const FIELDS = ['source', 'medium', 'campaign', 'content', 'term'];
const LIMITS = { source: 80, medium: 80, campaign: 120, content: 120, term: 120 };

function store() {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

function sessionId() {
  const s = store();
  const existing = s && s.getItem('snt_session_id');
  if (existing) return existing;
  const id = (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`).slice(0, 80);
  try { s && s.setItem('snt_session_id', id); } catch { /* private mode */ }
  return id;
}

function clip(v, n) { return typeof v === 'string' && v.trim() ? v.trim().slice(0, n) : null; }

function isExternalReferrer(ref) {
  if (!ref) return false;
  try { return new URL(ref).host !== window.location.host; } catch { return false; }
}

/** Parse `utm_*` (and the common `fbclid` / `gclid` click ids as content) out of a search string. */
export function parseUtm(search) {
  const p = new URLSearchParams(search || '');
  const out = {};
  for (const f of FIELDS) { const v = clip(p.get(`utm_${f}`), LIMITS[f]); if (v) out[f] = v; }
  if (!out.content) {
    if (p.get('fbclid')) out.content = clip(`fbclid:${p.get('fbclid')}`, LIMITS.content);
    else if (p.get('gclid')) out.content = clip(`gclid:${p.get('gclid')}`, LIMITS.content);
  }
  if (!out.source && p.get('fbclid')) { out.source = 'facebook'; out.medium = out.medium || 'paid'; }
  if (!out.source && p.get('gclid')) { out.source = 'google'; out.medium = out.medium || 'cpc'; }
  return Object.keys(out).length ? out : null;
}

/**
 * First-touch capture. Called on boot and on every navigation; only the first hit with UTM (or the first landing
 * with an external referrer) is kept for the session. Returns the stored record.
 */
export function captureAttribution(loc = typeof window !== 'undefined' ? window.location : null) {
  const s = store();
  if (!s || !loc) return null;
  const existing = readAttribution();
  const utm = parseUtm(loc.search);
  if (existing && (existing.source || !utm)) return existing;   // first touch with UTM wins; a referrer-only record upgrades once
  const referrer = typeof document !== 'undefined' && isExternalReferrer(document.referrer) ? document.referrer.slice(0, 1000) : null;
  if (!utm && !referrer) return existing;
  const rec = { ...(utm || {}), referrer: referrer || (existing && existing.referrer) || null, landed_at: (existing && existing.landed_at) || new Date().toISOString(), session_id: sessionId() };
  try { s.setItem(KEY, JSON.stringify(rec)); } catch { /* private mode */ }
  return rec;
}

export function readAttribution() {
  const s = store();
  if (!s) return null;
  try { const raw = s.getItem(KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

/** The `utm` object for POST /bookings — null when nothing was captured (the server then writes no row). */
export function utmPayload() {
  const a = readAttribution();
  if (!a) return null;
  const out = {};
  for (const f of FIELDS) if (a[f]) out[f] = a[f];
  if (a.referrer) out.referrer = a.referrer;
  if (a.landed_at) out.landed_at = a.landed_at;
  if (Object.keys(out).length === 0) return null;
  out.session_id = a.session_id || sessionId();
  return out;
}

export function attributionSummary() {
  const a = readAttribution();
  if (!a) return null;
  if (a.source) return `${a.source}${a.medium ? ` · ${a.medium}` : ''}${a.campaign ? ` · ${a.campaign}` : ''}`;
  if (a.referrer) { try { return new URL(a.referrer).host; } catch { return null; } }
  return null;
}

export function clearAttribution() { try { store()?.removeItem(KEY); } catch { /* noop */ } }

export default { captureAttribution, readAttribution, utmPayload, attributionSummary, parseUtm, clearAttribution };
